import { defineStore } from 'pinia';
import axios from '@/api/config/axios';
import { DEFAULT_COURSE_IMAGE_URL } from '@/utils/imageFallback';
import { isLessonVideoType } from '@/utils/lessonType';

export const useCursoStore = defineStore('cursoStore', {
    state: () => ({
        cursos: [],
        curso: null,
        cursoEnrollment: null, // Matrícula do curso atual (se houver)
        lastLesson: null, // Última aula acessada do curso atual
        meusCursos: [],
        pagination: {
            total: 0,
            per_page: 20,
            current_page: 1,
            last_page: 1,
            from: null,
            to: null
        },
        meusCursosPagination: {
            total: 0,
            per_page: 20,
            current_page: 1,
            last_page: 1,
            from: null,
            to: null
        },
        loading: false,
        meusCursosLoading: false,
        error: null,
        meusCursosError: null,
        // Estados para matrícula
        enrollmentLoading: false,
        enrollmentError: null,
        purchase: null, // Dados da compra (para cursos pagos)
        enrollment: null, // Dados da matrícula (para cursos gratuitos)
        // Estados para aula
        aula: null,
        aulaLoading: false,
        aulaError: null,
        aulaProgressSaving: false,
        aulaNoteSaving: false,
        quizSubmitting: false,
        simuladoSubmitting: false,
        simuladoLoading: false,
        // Estados para avaliação
        minhaAvaliacao: null, // Avaliação do usuário logado para o curso atual
        avaliacaoSubmitting: false,
        // Busca da barra do header (usada em / e /cursos)
        headerSearchQuery: '',
    }),

    getters: {
        getCursos: (state) => ({
            data: state.cursos,
            pagination: state.pagination
        }),
        
        currentPage: (state) => state.pagination.current_page,
        totalPages: (state) => state.pagination.last_page,
        totalRegistros: (state) => state.pagination.total,
        registrosPorPagina: (state) => state.pagination.per_page,
        
        getCurso: (state) => state.curso,
        getMeusCursos: (state) => ({
            data: state.meusCursos,
            pagination: state.meusCursosPagination
        }),
        isLoading: (state) => state.loading,
        isLoadingMeusCursos: (state) => state.meusCursosLoading,
        getError: (state) => state.error,
        getMeusCursosError: (state) => state.meusCursosError,
        getPurchase: (state) => state.purchase,
        getEnrollment: (state) => state.enrollment,
        getCursoEnrollment: (state) => state.cursoEnrollment,
        isEnrolled: (state) => {
            // Verifica se há matrícula ativa
            if (!state.cursoEnrollment) return false;
            // Se for objeto, verifica se tem status active ou se is_enrolled é true
            if (typeof state.cursoEnrollment === 'object') {
                return state.cursoEnrollment.status === 'active' || 
                       state.cursoEnrollment.is_enrolled === true ||
                       state.cursoEnrollment.id !== null;
            }
            return true;
        },
        isEnrolling: (state) => state.enrollmentLoading,
        getEnrollmentError: (state) => state.enrollmentError,
        getAula: (state) => state.aula,
        isAulaLoading: (state) => state.aulaLoading,
        getAulaError: (state) => state.aulaError,
        getLastLesson: (state) => state.lastLesson,
    },

    actions: {
        /**
         * Listagem pública de cursos (GET /api/v1/cursos).
         * Sem autenticação; retorna somente cursos com status published.
         * Params: level, is_featured, target_audience, instructor_id, search, orderBy, ascending, per_page, page.
         */
        async fetchCursos(params = {}) {
            this.loading = true;
            this.error = null;
            
            try {
                const response = await axios.get('/api/v1/cursos', { 
                    params: {
                        page: params.page || 1,
                        per_page: params.per_page || 20,
                        search: params.search || '',
                        level: params.level || '',
                        is_featured: params.is_featured ?? params.featured ?? '',
                        target_audience: params.target_audience || '',
                        instructor_id: params.instructor_id || '',
                        orderBy: params.orderBy || '',
                        ascending: params.ascending ?? '',
                        ...params
                    }
                });
                
                // Verifica se a resposta tem a estrutura esperada
                if (response.data.success && response.data.data) {
                    const { data, pagination } = response.data.data;
                    
                    // Mapear os dados da API para o formato esperado pelo template
                    this.cursos = data.map(curso => ({
                        id: curso.id,
                        titulo: curso.title,
                        slug: curso.slug,
                        descricao: curso.description,
                        imagem: this.getCourseImageFromPayload(curso),
                        avaliacao: parseFloat(curso.rating) || 0,
                        totalAvaliacoes: curso.total_reviews || 0,
                        totalAulas: curso.total_lessons || 0,
                        duracao: this.formatDuration(curso.total_duration || 0),
                        nivel: this.capitalizeLevel(curso.level || 'iniciante'),
                        totalAlunos: curso.total_students || 0,
                        destaque: curso.is_featured || false,
                        progresso: curso.progress || 0, // Se vier da API
                        price: parseFloat(curso.price) || 0,
                        sale_price: parseFloat(curso.sale_price) || null,
                        final_price: parseFloat(curso.final_price) || parseFloat(curso.price) || 0,
                        has_discount: curso.has_discount || false,
                        instructor: curso.instructor || null,
                        status: curso.status || 'draft',
                        target_audience: curso.target_audience || null,
                        created_at: curso.created_at
                    }));
                    
                    this.pagination = pagination || {
                        total: data.length,
                        per_page: data.length,
                        current_page: 1,
                        last_page: 1,
                        from: 1,
                        to: data.length
                    };
                    
                    return this.cursos;
                } else {
                    throw new Error('Estrutura de resposta inválida');
                }
            } catch (error) {
                this.error = 'Erro ao obter cursos';
                console.error('Erro na busca de cursos:', error);
                this.cursos = [];
                throw error;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Obtém detalhes do curso.
         * @param {string} cursoId - ID do curso
         * @param {object} options - { public: true } para usar rota pública GET /api/v1/cursos/:id (sem auth, só published)
         */
        async obterCurso(cursoId, options = {}) {
            this.loading = true;
            this.error = null;
            this.curso = null; // Limpar curso anterior
            this.lastLesson = null; // Limpar última aula ao carregar novo curso
            
            const usePublic = options.public === true;
            const url = usePublic ? `/api/v1/cursos/${cursoId}` : `/api/v1/courses/${cursoId}`;
            
            try {
                const response = await axios.get(url);
                
                if (response.data.success && response.data.data) {
                    const curso = response.data.data;
                    
                    // Mapear módulos e aulas
                    const modulos = (curso.modules || []).map(modulo => ({
                        id: modulo.id,
                        titulo: modulo.title,
                        descricao: modulo.description || '',
                        duracao: this.formatDuration(modulo.duration || 0),
                        progresso: modulo.progress || 0,
                        order_index: modulo.order_index || 0,
                        is_free: modulo.is_free || false,
                        aulas: (modulo.lessons || []).map(aula => ({
                            id: aula.id,
                            titulo: aula.title,
                            slug: aula.slug,
                            descricao: aula.description || '',
                            duracao: this.formatDuration(aula.duration || 0),
                            tipo: aula.type ? this.mapLessonType(aula.type) : null,
                            tipoOriginal: aula.type ?? null,
                            completa: aula.completed || false,
                            videoUrl: this.getVideoUrl(aula),
                            pdfUrl: aula.pdf_url || aula.pdf_upload?.url || null,
                            pdf_upload: aula.pdf_upload || null,
                            enunciado: aula.enunciado || '',
                            thumbnail: aula.thumbnail || null,
                            conteudo: aula.content || null,
                            video_provider: aula.video_provider || null,
                            video_id: aula.video_id || null,
                            is_preview: aula.is_preview || false,
                            is_published: aula.is_published !== false,
                            order_index: aula.order_index || 0,
                            materialApoio: aula.materials || []
                        })).sort((a, b) => a.order_index - b.order_index)
                    })).sort((a, b) => a.order_index - b.order_index);
                    
                    // Mapear instrutor
                    const instructor = curso.instructor ? {
                        id: curso.instructor.id,
                        nome: curso.instructor.name || curso.instructor.nome || null,
                        email: curso.instructor.email || null,
                        titulo: curso.instructor.title || curso.instructor.titulo || 'Instrutor',
                        profilePic: curso.instructor.profile_pic || curso.instructor.profilePic || null,
                        totalCursos: curso.instructor.totalCursos || curso.instructor.total_courses || 0,
                        totalAlunos: curso.instructor.totalAlunos || curso.instructor.total_students || 0,
                        avaliacao: parseFloat(curso.instructor.avaliacao || curso.instructor.rating) || 0,
                        bio: curso.instructor.bio || ''
                    } : null;
                    
                    // Verificar se há matrícula do usuário
                    // Considera tanto o campo enrollment quanto is_enrolled
                    if (curso.is_enrolled === true || curso.enrollment) {
                        this.cursoEnrollment = curso.enrollment || {
                            id: 'enrollment-id',
                            enrolled_at: null,
                            progress_percentage: curso.progress || 0,
                            status: 'active',
                            is_enrolled: true
                        };
                    } else {
                        this.cursoEnrollment = null;
                    }

                    // Capturar última aula acessada
                    // Pode vir de curso.last_lesson ou curso.enrollment.last_lesson
                    if (curso.last_lesson) {
                        this.lastLesson = {
                            id: curso.last_lesson.id,
                            title: curso.last_lesson.title,
                            slug: curso.last_lesson.slug,
                            module_id: curso.last_lesson.module_id || null,
                            order_index: curso.last_lesson.order_index || 0
                        };
                    } else if (curso.enrollment && curso.enrollment.last_lesson) {
                        this.lastLesson = {
                            id: curso.enrollment.last_lesson.id,
                            title: curso.enrollment.last_lesson.title,
                            slug: curso.enrollment.last_lesson.slug,
                            module_id: curso.enrollment.last_lesson.module_id || null,
                            order_index: curso.enrollment.last_lesson.order_index || 0
                        };
                    } else {
                        this.lastLesson = null;
                    }
                    
                    // Verificar se o usuário já avaliou o curso
                    // Primeiro tenta usar my_review do endpoint de detalhes (fallback)
                    const minhaAvaliacao = curso.my_review || null;
                    if (minhaAvaliacao) {
                        this.minhaAvaliacao = {
                            id: minhaAvaliacao.id,
                            rating: minhaAvaliacao.rating || 0,
                            comment: minhaAvaliacao.comment || '',
                            is_approved: minhaAvaliacao.is_approved !== undefined ? minhaAvaliacao.is_approved : null,
                            created_at: minhaAvaliacao.created_at || null
                        };
                    } else {
                        this.minhaAvaliacao = null;
                    }
                    
                    // Mapear para o formato esperado
                    this.curso = {
                        id: curso.id,
                        titulo: curso.title,
                        slug: curso.slug,
                        descricao: curso.description,
                        descricaoCompleta: curso.full_description || curso.description,
                        imagem: this.getCourseImageFromPayload(curso),
                        avaliacao: parseFloat(curso.rating) || 0,
                        totalAulas: curso.total_lessons || 0,
                        duracao: this.formatDuration(curso.total_duration || 0),
                        nivel: this.capitalizeLevel(curso.level || 'iniciante'),
                        totalAlunos: curso.total_students || 0,
                        totalAvaliacoes: curso.total_reviews || 0,
                        distribuicaoAvaliacoes: this.processarDistribuicaoAvaliacoes(
                            curso.rating_distribution || curso.distribuicaoAvaliacoes,
                            curso.total_reviews || 0
                        ),
                        destaque: curso.is_featured || false,
                        price: parseFloat(curso.price) || 0,
                        sale_price: parseFloat(curso.sale_price) || null,
                        final_price: parseFloat(curso.final_price) || parseFloat(curso.price) || 0,
                        has_discount: curso.has_discount || false,
                        discount_percentage: curso.discount_percentage || 0,
                        currency: curso.currency || 'BRL',
                        instructor: instructor,
                        modulos: modulos,
                        avaliacoes: curso.reviews || [],
                        sobre: curso.full_description || curso.description || '',
                        aprendizado: curso.what_you_will_learn || [],
                        requisitos: curso.requirements || [],
                        progresso: curso.progress || (this.cursoEnrollment ? parseFloat(this.cursoEnrollment.progress_percentage) : 0),
                        target_audience: curso.target_audience || null,
                        language: curso.language || 'pt-BR',
                        lastLesson: this.lastLesson, // Incluir última aula no objeto curso também
                        status: curso.status || 'draft'
                    };
                    
                    return this.curso;
                } else {
                    this.error = 'Curso não encontrado';
                    this.curso = null;
                    return null;
                }
            } catch (error) {
                console.error('Erro ao obter curso:', error);
                
                // Tratar diferentes tipos de erro
                if (error.response) {
                    if (error.response.status === 404) {
                        this.error = 'Curso não encontrado';
                    } else if (error.response.status === 403) {
                        this.error = 'Você não tem permissão para acessar este curso';
                    } else {
                        this.error = error.response.data?.message || 'Erro ao carregar curso';
                    }
                } else if (error.request) {
                    this.error = 'Erro de conexão. Verifique sua internet.';
                } else {
                    this.error = 'Erro ao obter curso';
                }
                
                this.curso = null;
                // Não lançar erro, apenas definir o estado
                return null;
            } finally {
                this.loading = false;
            }
        },

        setHeaderSearchQuery(query) {
            this.headerSearchQuery = typeof query === 'string' ? query : '';
        },

        formatDuration(minutes) {
            if (!minutes || minutes === 0) return '0 min';
            
            if (minutes < 60) {
                return `${minutes} min`;
            }
            
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            
            if (mins === 0) {
                return `${hours}h`;
            }
            
            return `${hours}h ${mins}min`;
        },

        capitalizeLevel(level) {
            if (!level) return 'Iniciante';
            
            const levelLower = level.toLowerCase();
            const levels = {
                'iniciante': 'Iniciante',
                'intermediario': 'Intermediário',
                'intermediário': 'Intermediário',
                'avancado': 'Avançado',
                'avançado': 'Avançado'
            };
            
            return levels[levelLower] || level.charAt(0).toUpperCase() + level.slice(1);
        },

        /**
         * Primeira URL não vazia entre candidatos; senão placeholder público.
         * Aceita vários nomes de campo do backend (lista/detalhe podem diferir).
         */
        getImageUrl(...sources) {
            for (const raw of sources) {
                if (raw == null) continue;
                const u = typeof raw === 'string' ? raw.trim() : String(raw).trim();
                if (!u || u === 'null' || u === 'undefined') continue;
                if (u.startsWith('http://') || u.startsWith('https://')) return u;
                return u;
            }
            return DEFAULT_COURSE_IMAGE_URL;
        },

        getCourseImageFromPayload(payload) {
            if (!payload || typeof payload !== 'object') return DEFAULT_COURSE_IMAGE_URL;
            return this.getImageUrl(
                payload.thumbnail,
                payload.image,
                payload.thumbnail_url,
                payload.cover_image,
                payload.cover_url,
                payload.banner,
                payload.banner_url,
                payload.imagem,
                payload.imagem_url,
                payload.capa,
                payload.capa_url
            );
        },

        mapLessonType(type) {
            // Mapear tipos da API para o formato esperado pelo template
            const typeMap = {
                'video': 'video',
                'text': 'texto',
                'quiz': 'quiz',
                'assignment': 'exercicio',
                'live': 'live',
                'simulado': 'simulado',
                'pdf': 'pdf'
            };
            
            return typeMap[type] || type;
        },

        getVideoUrl(aula) {
            // Se tiver video_url direto
            if (aula.video_url) {
                return aula.video_url;
            }

            // API Laravel (self_hosted): URL assinada em video_upload
            const uploadUrl = aula.video_upload && typeof aula.video_upload === 'object'
                ? aula.video_upload.url
                : null;
            if (uploadUrl) {
                return uploadUrl;
            }
            
            // Se for YouTube
            if (aula.video_provider === 'youtube' && aula.video_id) {
                return `https://www.youtube.com/embed/${aula.video_id}`;
            }
            
            // Se for Vimeo
            if (aula.video_provider === 'vimeo' && aula.video_id) {
                return `https://player.vimeo.com/video/${aula.video_id}`;
            }
            
            // Se for self-hosted
            if (aula.video_provider === 'self_hosted' && aula.video_id) {
                return aula.video_id; // Assume que video_id contém a URL
            }
            
            return null;
        },

        async fetchMeusCursos(params = {}) {
            this.meusCursosLoading = true;
            this.meusCursosError = null;
            
            try {
                const response = await axios.get('/api/v1/courses/my-enrolled/list', { 
                    params: {
                        status: params.status || 'published',
                        per_page: params.per_page || 10,
                        page: params.page || 1,
                        search: params.search || '',
                        ...params
                    }
                });
                
                if (response.data.success) {
                    // A API pode retornar data diretamente ou dentro de data.data
                    let data = [];
                    let pagination = null;
                    
                    if (Array.isArray(response.data.data)) {
                        // Se data é um array direto
                        data = response.data.data;
                        pagination = response.data.pagination || null;
                    } else if (response.data.data && Array.isArray(response.data.data.data)) {
                        // Se data está dentro de data.data
                        data = response.data.data.data;
                        pagination = response.data.data.pagination || response.data.pagination || null;
                    } else if (response.data.data) {
                        // Tentar pegar como objeto único
                        data = [response.data.data];
                        pagination = response.data.pagination || null;
                    }
                    
                    // Mapear os dados da API para o formato esperado pelo template
                    this.meusCursos = data.map(item => {
                        const enrollment = item.enrollment || {};
                        const progresso = parseFloat(enrollment.progress_percentage) || 0;
                        
                        // Calcular aulas concluídas
                        // Se a API retornar completed_lessons, usar esse valor, senão calcular
                        const totalAulas = item.total_lessons || 0;
                        const aulasConcluidas = enrollment.completed_lessons !== undefined 
                            ? enrollment.completed_lessons 
                            : Math.round((progresso / 100) * totalAulas);
                        
                        // Calcular tempo restante
                        const totalDuration = item.total_duration || 0;
                        const watchedDuration = enrollment.watched_duration || Math.round((progresso / 100) * totalDuration);
                        const remainingDuration = totalDuration - watchedDuration;
                        
                        // Formatar última aula acessada
                        let ultimaAula = null;
                        let lastLesson = null;
                        if (enrollment.last_lesson) {
                            // Se tiver objeto last_lesson
                            const lastLessonObj = enrollment.last_lesson;
                            ultimaAula = lastLessonObj.title || `Aula ${lastLessonObj.order_index || ''}: ${lastLessonObj.title || ''}`;
                            lastLesson = {
                                id: lastLessonObj.id,
                                title: lastLessonObj.title,
                                slug: lastLessonObj.slug,
                                module_id: lastLessonObj.module_id || null,
                                order_index: lastLessonObj.order_index || 0
                            };
                        } else if (enrollment.last_lesson_title) {
                            // Se tiver apenas o título
                            ultimaAula = enrollment.last_lesson_title;
                        } else if (enrollment.last_accessed_at) {
                            // Fallback para data de último acesso
                            ultimaAula = `Último acesso: ${this.formatDate(enrollment.last_accessed_at)}`;
                        }
                        
                        return {
                            id: item.id,
                            course_id: item.id,
                            enrollment_id: enrollment.id || null,
                            titulo: item.title,
                            slug: item.slug,
                            descricao: item.description,
                            imagem: this.getCourseImageFromPayload(item),
                            nivel: this.capitalizeLevel(item.level || 'iniciante'),
                            progresso: Math.round(progresso),
                            totalAulas: totalAulas,
                            aulasConcluidas: aulasConcluidas,
                            tempoRestante: this.formatRemainingTime(remainingDuration),
                            ultimaAula: ultimaAula,
                            lastLesson: lastLesson, // Incluir objeto lastLesson para navegação
                            status: enrollment.status || 'active',
                            enrolled_at: enrollment.enrolled_at,
                            last_accessed_at: enrollment.last_accessed_at,
                            completed_at: enrollment.completed_at,
                            expires_at: enrollment.expires_at,
                            price: parseFloat(item.price) || 0,
                            sale_price: parseFloat(item.sale_price) || null,
                            final_price: parseFloat(item.final_price) || parseFloat(item.price) || 0,
                            instructor: item.instructor || null,
                            rating: parseFloat(item.rating) || 0,
                            target_audience: item.target_audience || null,
                        };
                    });
                    
                    // Paginação
                    if (pagination) {
                        this.meusCursosPagination = pagination;
                    } else {
                        this.meusCursosPagination = {
                            total: data.length,
                            per_page: data.length,
                            current_page: 1,
                            last_page: 1,
                            from: 1,
                            to: data.length
                        };
                    }
                    
                    return this.meusCursos;
                } else {
                    throw new Error('Estrutura de resposta inválida');
                }
            } catch (error) {
                this.meusCursosError = 'Erro ao obter meus cursos';
                console.error('Erro na busca de meus cursos:', error);
                this.meusCursos = [];
                throw error;
            } finally {
                this.meusCursosLoading = false;
            }
        },

        formatRemainingTime(minutes) {
            if (!minutes || minutes <= 0) return 'Concluído';
            
            if (minutes < 60) {
                return `${minutes} min restantes`;
            }
            
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            
            if (hours < 24) {
                if (mins === 0) {
                    return `${hours}h restantes`;
                }
                return `${hours}h ${mins}min restantes`;
            }
            
            const days = Math.floor(hours / 24);
            const remainingHours = hours % 24;
            
            if (days === 1) {
                return remainingHours > 0 
                    ? `1 dia e ${remainingHours}h restantes`
                    : '1 dia restante';
            }
            
            return remainingHours > 0
                ? `${days} dias e ${remainingHours}h restantes`
                : `${days} dias restantes`;
        },

        formatDate(dateString) {
            if (!dateString) return '';
            
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);
            
            if (diffMins < 1) return 'Agora';
            if (diffMins < 60) return `Há ${diffMins} min`;
            if (diffHours < 24) return `Há ${diffHours}h`;
            if (diffDays === 1) return 'Ontem';
            if (diffDays < 7) return `Há ${diffDays} dias`;
            if (diffDays < 30) {
                const weeks = Math.floor(diffDays / 7);
                return `Há ${weeks} ${weeks === 1 ? 'semana' : 'semanas'}`;
            }
            if (diffDays < 365) {
                const months = Math.floor(diffDays / 30);
                return `Há ${months} ${months === 1 ? 'mês' : 'meses'}`;
            }
            
            const years = Math.floor(diffDays / 365);
            return `Há ${years} ${years === 1 ? 'ano' : 'anos'}`;
        },

        limpar() {
            this.curso = null;
            this.error = null;
        },

        limparMeusCursos() {
            this.meusCursos = [];
            this.meusCursosError = null;
        },

        async matricularEmCurso(courseId, dadosPagamento = {}) {
            this.enrollmentLoading = true;
            this.enrollmentError = null;
            this.purchase = null;
            this.enrollment = null;
            
            try {
                const response = await axios.post(`/api/v1/courses/${courseId}/enroll`, dadosPagamento);
                
                // Verificar se a resposta indica erro (success: false)
                if (response.data && response.data.success === false) {
                    const responseData = response.data;
                    
                    // Extrair mensagem de erro
                    let errorMessage = null;
                    
                    if (responseData.errors?.error) {
                        errorMessage = responseData.errors.error;
                    } else if (responseData.errors && typeof responseData.errors === 'object') {
                        const firstErrorKey = Object.keys(responseData.errors)[0];
                        const firstError = responseData.errors[firstErrorKey];
                        
                        if (Array.isArray(firstError)) {
                            errorMessage = firstError[0];
                        } else if (typeof firstError === 'string') {
                            errorMessage = firstError;
                        }
                    } else if (responseData.message) {
                        errorMessage = responseData.message;
                    }
                    
                    this.enrollmentError = errorMessage || 'Erro ao realizar matrícula';
                    throw new Error(errorMessage || 'Erro ao realizar matrícula');
                }
                
                if (response.data.success && response.data.data) {
                    const data = response.data.data;
                    
                    // Verificar se é matrícula direta (curso gratuito) ou compra (curso pago)
                    if (data.status === 'active' && data.enrolled_at) {
                        // Matrícula criada imediatamente (curso gratuito)
                        this.enrollment = {
                            id: data.id,
                            enrolled_at: data.enrolled_at,
                            progress_percentage: parseFloat(data.progress_percentage) || 0,
                            status: data.status,
                            course: data.course || null,
                            user: data.user || null
                        };
                        return { type: 'enrollment', data: this.enrollment };
                    } else if (data.status === 'pending' || data.payment_data) {
                        // Compra criada (curso pago)
                        this.purchase = {
                            id: data.id,
                            course_id: data.course_id,
                            course: data.course || null,
                            valor_curso: parseFloat(data.valor_curso) || 0,
                            valor_original: parseFloat(data.valor_original) || 0,
                            desconto_aplicado: parseFloat(data.desconto_aplicado) || 0,
                            percentual_desconto: parseFloat(data.percentual_desconto) || 0,
                            forma_pagamento: data.forma_pagamento || 'PIX',
                            payment_provider: data.payment_provider || data.payment_data?.payment_provider || null,
                            status: data.status,
                            data_vencimento: data.data_vencimento,
                            payment_data: data.payment_data || {}
                        };
                        return { type: 'purchase', data: this.purchase };
                    }
                    
                    throw new Error('Resposta inválida do servidor');
                } else {
                    throw new Error('Erro ao processar matrícula');
                }
            } catch (error) {
                console.error('Erro ao matricular em curso:', error);
                
                if (error.response) {
                    const responseData = error.response.data || {};
                    
                    // Extrair mensagem de erro - verificar diferentes formatos
                    let errorMessage = null;
                    
                    // Primeiro tenta pegar de errors.error (formato específico)
                    if (responseData.errors?.error) {
                        errorMessage = responseData.errors.error;
                    }
                    // Se errors é um objeto com outras chaves
                    else if (responseData.errors && typeof responseData.errors === 'object') {
                        // Pega o primeiro erro do objeto
                        const firstErrorKey = Object.keys(responseData.errors)[0];
                        const firstError = responseData.errors[firstErrorKey];
                        
                        // Se for array, pega o primeiro item
                        if (Array.isArray(firstError)) {
                            errorMessage = firstError[0];
                        } else if (typeof firstError === 'string') {
                            errorMessage = firstError;
                        }
                    }
                    // Tenta pegar de message
                    else if (responseData.message) {
                        errorMessage = responseData.message;
                    }
                    
                    // Se não encontrou mensagem específica, usa mensagens padrão por status
                    if (!errorMessage) {
                        if (error.response.status === 400) {
                            errorMessage = 'Dados inválidos';
                        } else if (error.response.status === 403) {
                            errorMessage = 'Você não tem permissão para se matricular neste curso';
                        } else if (error.response.status === 409) {
                            errorMessage = 'Você já está matriculado neste curso';
                        } else {
                            errorMessage = 'Erro ao realizar matrícula';
                        }
                    }
                    
                    this.enrollmentError = errorMessage;
                } else if (error.request) {
                    this.enrollmentError = 'Erro de conexão. Verifique sua internet.';
                } else {
                    this.enrollmentError = error.message || 'Erro ao realizar matrícula';
                }
                
                throw error;
            } finally {
                this.enrollmentLoading = false;
            }
        },

        async obterInformacoesLinkCompartilhamento(token) {
            if (!token) {
                throw new Error('Token do link não informado.');
            }

            const response = await axios.get(`/api/v1/cursos/entrar/${token}`);
            return response.data;
        },

        async entrarCursoPorLink(token, payload = {}) {
            if (!token) {
                throw new Error('Token do link não informado.');
            }

            this.enrollmentLoading = true;
            this.enrollmentError = null;
            this.purchase = null;
            this.enrollment = null;

            try {
                const response = await axios.post(`/api/v1/cursos/entrar/${token}`, payload);

                if (!response.data?.success) {
                    const errorMessage = response.data?.message || 'Não foi possível processar este link.';
                    this.enrollmentError = errorMessage;
                    throw new Error(errorMessage);
                }

                const data = response.data.data || {};
                const tipoFluxo = data.tipo_fluxo;

                if (tipoFluxo === 'gratuito') {
                    this.enrollment = {
                        id: data.enrollment_id || null,
                        course_id: data.course_id || null,
                        course_title: data.course_title || null,
                        user_id: data.user_id || null,
                        status: 'active'
                    };

                    return { type: 'enrollment', data: this.enrollment, raw: data };
                }

                if (tipoFluxo === 'pago') {
                    this.purchase = {
                        id: data.purchase_id || null,
                        course_id: data.course_id || null,
                        course: data.course_title ? { title: data.course_title } : null,
                        valor_curso: parseFloat(data.valor) || 0,
                        valor_original: parseFloat(data.valor) || 0,
                        desconto_aplicado: 0,
                        percentual_desconto: 0,
                        forma_pagamento: data.forma_pagamento || 'PIX',
                        payment_provider: data.payment_provider || null,
                        status: data.status || 'pending',
                        data_vencimento: data.data_vencimento || null,
                        payment_data: {
                            link_pagamento: data.link_pagamento || null,
                            pix_qr_code: data.pix_qr_code || null,
                            pix_qr_code_base64: data.pix_qr_code_base64 || null,
                            payment_provider: data.payment_provider || null,
                        }
                    };

                    return { type: 'purchase', data: this.purchase, raw: data };
                }

                return { type: 'unknown', data, raw: data };
            } catch (error) {
                if (error.response?.data) {
                    const apiMessage = error.response.data.message || error.response.data.errors?.error;
                    this.enrollmentError = apiMessage || this.enrollmentError || 'Não foi possível processar este link.';
                } else if (!this.enrollmentError) {
                    this.enrollmentError = error.message || 'Não foi possível processar este link.';
                }
                throw error;
            } finally {
                this.enrollmentLoading = false;
            }
        },

        async verificarStatusCompra(purchaseId) {
            try {
                // Tentar endpoint de status da compra
                const response = await axios.get(`/api/v1/purchases/${purchaseId}/status`);
                
                if (response.data.success && response.data.data) {
                    const purchaseData = response.data.data;
                    
                    // Atualizar purchase no store com todos os dados atualizados
                    if (this.purchase && this.purchase.id === purchaseId) {
                        this.purchase.status = purchaseData.status;
                        this.purchase.data_vencimento = purchaseData.data_vencimento;
                        this.purchase.updated_at = purchaseData.updated_at;
                        
                        // Atualizar payment_data se disponível
                        if (purchaseData.payment_data) {
                            this.purchase.payment_data = {
                                ...this.purchase.payment_data,
                                ...purchaseData.payment_data
                            };
                        }
                        
                        // Se pagamento foi confirmado, recarregar curso para verificar matrícula
                        if (purchaseData.status === 'paid') {
                            // Aguardar um pouco para o webhook processar
                            setTimeout(() => {
                                if (this.curso) {
                                    this.obterCurso(this.curso.id);
                                }
                                // Recarregar meus cursos também
                                this.fetchMeusCursos();
                            }, 2000);
                        }
                    }
                    
                    return purchaseData;
                }
                
                throw new Error('Resposta inválida');
            } catch (error) {
                // Se o endpoint não existir, não é crítico
                if (error.response && error.response.status === 404) {
                    console.warn('Endpoint de status da compra não encontrado');
                    return null;
                }
                console.error('Erro ao verificar status da compra:', error);
                throw error;
            }
        },

        limparMatricula() {
            this.purchase = null;
            this.enrollment = null;
            this.enrollmentError = null;
        },

        // ========== ACTIONS PARA AULA ==========

        async obterAula(courseId, lessonId) {
            this.aulaLoading = true;
            this.aulaError = null;
            this.aula = null;

            try {
                const response = await axios.get(`/api/v1/courses/${courseId}/lessons/${lessonId}`);

                if (response.data.success && response.data.data) {
                    const aulaData = response.data.data;

                    // Mapear dados da aula
                    this.aula = {
                        id: aulaData.id,
                        titulo: aulaData.title,
                        slug: aulaData.slug,
                        descricao: aulaData.description || '',
                        tipo: aulaData.type ? this.mapLessonType(aulaData.type) : null,
                        tipoOriginal: aulaData.type ?? null,
                        conteudo: aulaData.content || null,
                        enunciado: aulaData.enunciado || '',
                        videoUrl: this.getVideoUrl(aulaData),
                        pdfUrl: aulaData.pdf_url || aulaData.pdf_upload?.url || null,
                        pdf_upload: aulaData.pdf_upload || null,
                        video_upload: aulaData.video_upload || null,
                        video_provider: aulaData.video_provider || null,
                        video_id: aulaData.video_id || null,
                        thumbnail: aulaData.thumbnail || null,
                        duracao: this.formatDuration(aulaData.duration || 0),
                        order_index: aulaData.order_index || 0,
                        is_preview: aulaData.is_preview || false,
                        is_published: aulaData.is_published !== false,
                        completa: aulaData.completed || false,
                        progress_percentage: parseFloat(aulaData.progress_percentage) || 0,
                        current_time: parseFloat(aulaData.current_time) || 0,
                        last_accessed_at: aulaData.last_accessed_at || null,
                        live_starts_at: aulaData.live_starts_at || null,
                        live_ends_at: aulaData.live_ends_at || null,
                        has_full_access: aulaData.has_full_access !== undefined ? aulaData.has_full_access : true,
                        materialApoio: (aulaData.materials || []).map(material => ({
                            id: material.id,
                            nome: material.name,
                            tipo: material.type,
                            url: material.url,
                            file_size: material.file_size
                        })),
                        quiz: aulaData.quiz ? {
                            id: aulaData.quiz.id,
                            title: aulaData.quiz.title,
                            has_previous_submission: aulaData.quiz.has_previous_submission || false,
                            previous_attempt: aulaData.quiz.previous_attempt || null,
                            max_attempts: aulaData.quiz.max_attempts || null,
                            attempts_count: aulaData.quiz.attempts_count || 0,
                            remaining_attempts: aulaData.quiz.remaining_attempts || null,
                            can_retry: aulaData.quiz.can_retry !== undefined ? aulaData.quiz.can_retry : true,
                            questions: (aulaData.quiz.questions || []).map(question => {
                                // Mapear opções: se for array de objetos, converter para array de strings
                                let options = [];
                                let optionIds = [];
                                let correctAnswerIndex = null;
                                
                                if (question.options && Array.isArray(question.options)) {
                                    // Verificar se é array de objetos ou array de strings
                                    if (question.options.length > 0 && typeof question.options[0] === 'object') {
                                        // É array de objetos - ordenar primeiro
                                        const sortedOptions = [...question.options].sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
                                        
                                        // Converter para array de strings e encontrar índice correto
                                        options = sortedOptions.map((option, index) => {
                                            optionIds.push(option.id || null);
                                            // Se esta opção é a correta, salvar o índice
                                            if (option.is_correct === true && correctAnswerIndex === null) {
                                                correctAnswerIndex = index;
                                            }
                                            return option.option_text || option.text || option.label || String(option);
                                        });
                                    } else {
                                        // Já é array de strings
                                        options = question.options;
                                        // Se houver correct_answer_index, usar ele
                                        correctAnswerIndex = question.correct_answer_index !== undefined 
                                            ? question.correct_answer_index 
                                            : null;
                                    }
                                }
                                
                                // Se houver tentativa anterior, buscar resposta e explicação
                                let previousAnswerIndex = null;
                                let previousAnswerText = null;
                                let explanation = question.explanation || null;
                                
                                if (aulaData.quiz.previous_attempt && aulaData.quiz.previous_attempt.answers) {
                                    const previousAnswer = aulaData.quiz.previous_attempt.answers.find(
                                        a => a.question_id === question.id
                                    );
                                    if (previousAnswer) {
                                        previousAnswerIndex = previousAnswer.answer_index;
                                        previousAnswerText = previousAnswer.answer_text !== undefined
                                            ? previousAnswer.answer_text
                                            : (previousAnswer.answer !== undefined ? previousAnswer.answer : null);
                                        // Se não tiver explanation na questão, usar da resposta anterior
                                        if (!explanation && previousAnswer.explanation) {
                                            explanation = previousAnswer.explanation;
                                        }
                                        // Se não tiver correct_answer_index, usar da resposta anterior
                                        if (correctAnswerIndex === null && previousAnswer.correct_answer_index !== undefined) {
                                            correctAnswerIndex = previousAnswer.correct_answer_index;
                                        }
                                        // Em alguns quizzes antigos o backend devolve answer_index deslocado.
                                        // Quando a resposta foi correta e já sabemos a opção correta pelo enunciado,
                                        // usamos o índice correto para manter o estado visual coerente.
                                        if (
                                            previousAnswer.is_correct === true &&
                                            previousAnswerIndex !== null &&
                                            previousAnswerIndex !== undefined &&
                                            correctAnswerIndex !== null &&
                                            correctAnswerIndex !== undefined &&
                                            previousAnswerIndex !== correctAnswerIndex
                                        ) {
                                            previousAnswerIndex = correctAnswerIndex;
                                        }
                                    }
                                }
                                
                                return {
                                    id: question.id,
                                    question: question.question || question.text || '',
                                    type: question.type || 'multiple_choice',
                                    options: options,
                                    option_ids: optionIds.length > 0 ? optionIds : null,
                                    correct_answer_index: correctAnswerIndex !== null 
                                        ? correctAnswerIndex 
                                        : (question.correct_answer_index !== undefined ? question.correct_answer_index : null),
                                    previous_answer_index: previousAnswerIndex,
                                    previous_answer_text: previousAnswerText,
                                    explanation: explanation,
                                    order_index: question.order_index || 0
                                };
                            })
                        } : null,
                        module: aulaData.module || null,
                        course: aulaData.course || null,
                        note: aulaData.note || null,
                        previous_lesson: aulaData.previous_lesson || null,
                        next_lesson: aulaData.next_lesson || null,
                        simulado_submission_mode: ['text_only', 'image_only', 'both'].includes(
                            aulaData.simulado_submission_mode
                        )
                            ? aulaData.simulado_submission_mode
                            : 'both',
                    };

                    return this.aula;
                } else {
                    throw new Error('Resposta inválida do servidor');
                }
            } catch (error) {
                console.error('Erro ao obter aula:', error);
                
                if (error.response) {
                    if (error.response.status === 404) {
                        this.aulaError = 'Aula não encontrada';
                    } else if (error.response.status === 403) {
                        this.aulaError = 'Você não tem permissão para acessar esta aula';
                    } else {
                        this.aulaError = error.response.data?.message || 'Erro ao carregar aula';
                    }
                } else {
                    this.aulaError = 'Erro de conexão. Verifique sua internet.';
                }

                throw error;
            } finally {
                this.aulaLoading = false;
            }
        },

        async registrarAcessoAula(courseId, lessonId) {
            try {
                const response = await axios.post(`/api/v1/courses/${courseId}/lessons/${lessonId}/access`);
                
                // Verificar se a resposta indica erro mesmo com status 200
                if (response.data && response.data.success === false) {
                    const errorMessage = response.data.message || 'Erro ao registrar acesso à aula';
                    this.aulaError = errorMessage;
                    
                    // Se a aula já foi carregada, atualizar has_full_access para false
                    if (this.aula && this.aula.id === lessonId) {
                        this.aula.has_full_access = false;
                    }
                    
                    // Não limpar a aula - manter estrutura visível mas bloquear conteúdo
                    throw new Error(errorMessage);
                }
                
                // Atualizar last_accessed_at na aula local
                if (this.aula && this.aula.id === lessonId) {
                    this.aula.last_accessed_at = new Date().toISOString();
                }
            } catch (error) {
                console.error('Erro ao registrar acesso:', error);
                
                // Se for erro de resposta HTTP, definir aulaError
                if (error.response) {
                    const errorMessage = error.response.data?.message || 
                                       (error.response.status === 403 ? 'Você não tem permissão para acessar esta aula' : 
                                        error.response.status === 404 ? 'Aula não encontrada' : 
                                        'Erro ao registrar acesso à aula');
                    this.aulaError = errorMessage;
                    
                    // Se a aula já foi carregada, atualizar has_full_access para false
                    if (this.aula && this.aula.id === lessonId) {
                        this.aula.has_full_access = false;
                    }
                    
                    // Não limpar a aula - manter estrutura visível mas bloquear conteúdo
                } else if (error.message) {
                    // Se já tiver mensagem de erro (do throw acima), usar ela
                    this.aulaError = error.message;
                    
                    // Se a aula já foi carregada, atualizar has_full_access para false
                    if (this.aula && this.aula.id === lessonId) {
                        this.aula.has_full_access = false;
                    }
                    
                    // Não limpar a aula - manter estrutura visível mas bloquear conteúdo
                }
                
                throw error;
            }
        },

        async marcarAulaComoConcluida(courseId, lessonId, completed = true) {
            try {
                const response = await axios.post(
                    `/api/v1/courses/${courseId}/lessons/${lessonId}/complete`,
                    { completed }
                );

                // Verificar se a resposta indica erro mesmo com status 200
                if (response.data && response.data.success === false) {
                    // Extrair mensagem de erro da resposta
                    let errorMessage = response.data.message || 'Erro ao marcar aula como concluída';
                    
                    // Se não tiver message direto, tentar pegar de errors
                    if (!errorMessage || errorMessage === 'Erro ao marcar aula como concluída') {
                        if (response.data.errors) {
                            // Tentar pegar de errors.error
                            if (response.data.errors.error) {
                                errorMessage = response.data.errors.error;
                            }
                            // Tentar pegar de errors (objeto com múltiplos campos)
                            else if (typeof response.data.errors === 'object') {
                                const errorValues = Object.values(response.data.errors);
                                // Procurar primeiro valor que seja string
                                for (const errorValue of errorValues) {
                                    if (typeof errorValue === 'string' && errorValue) {
                                        errorMessage = errorValue;
                                        break;
                                    } else if (Array.isArray(errorValue) && errorValue.length > 0) {
                                        errorMessage = errorValue[0];
                                        break;
                                    }
                                }
                            }
                        }
                    }
                    
                    const error = new Error(errorMessage);
                    error.response = {
                        data: response.data,
                        status: response.status || 400
                    };
                    throw error;
                }

                if (response.data.success && response.data.data) {
                    const data = response.data.data;

                    // Atualizar aula local
                    if (this.aula && this.aula.id === lessonId) {
                        this.aula.completa = data.completed;
                        this.aula.progress_percentage = parseFloat(data.progress_percentage) || 0;
                    }

                    // Atualizar aula na lista de módulos do curso e recalcular progresso do módulo
                    if (this.curso && this.curso.modulos) {
                        this.curso.modulos.forEach(modulo => {
                            const aula = modulo.aulas.find(a => a.id === lessonId);
                            if (aula) {
                                aula.completa = data.completed;
                                aula.progress_percentage = parseFloat(data.progress_percentage) || 0;
                                
                                // Recalcular progresso do módulo baseado nas aulas
                                this.recalcularProgressoModulo(modulo);
                            }
                        });
                    }

                    // Atualizar progresso do curso se disponível
                    if (data.course_progress && this.curso) {
                        this.curso.progresso = parseFloat(data.course_progress.progress_percentage) || 0;
                    }

                    return data;
                }

                throw new Error('Resposta inválida');
            } catch (error) {
                console.error('Erro ao marcar aula como concluída:', error);
                throw error;
            }
        },

        async salvarProgressoVideo(courseId, lessonId, progressData) {
            const aulaRef = this.aula?.id === lessonId
                ? this.aula
                : this.curso?.modulos
                    ?.flatMap((modulo) => modulo.aulas || [])
                    .find((aula) => aula.id === lessonId);

            if (!isLessonVideoType(aulaRef)) {
                return null;
            }

            // Debounce: só salva se não estiver salvando
            if (this.aulaProgressSaving) {
                return;
            }

            this.aulaProgressSaving = true;

            try {
                // Calcular progress_percentage se não vier no progressData
                let progressPercentage = progressData.percent;
                
                if (progressPercentage === null || progressPercentage === undefined) {
                    // Calcular baseado em currentTime e duration
                    if (progressData.currentTime && progressData.duration && progressData.duration > 0) {
                        progressPercentage = (progressData.currentTime / progressData.duration) * 100;
                    } else {
                        progressPercentage = 0;
                    }
                }

                // Garantir que seja um número válido
                progressPercentage = parseFloat(progressPercentage) || 0;
                
                // Limitar entre 0 e 100
                progressPercentage = Math.max(0, Math.min(100, progressPercentage));

                let currentTime = parseFloat(progressData.currentTime) || 0;
                let duration = parseFloat(progressData.duration) || 0;

                if (duration > 0 && currentTime > duration) {
                    currentTime = duration;
                }

                const response = await axios.put(
                    `/api/v1/courses/${courseId}/lessons/${lessonId}/progress`,
                    {
                        current_time: currentTime,
                        duration,
                        progress_percentage: progressPercentage
                    }
                );

                if (response.data.success && response.data.data) {
                    const data = response.data.data;

                    // Atualizar aula local
                    if (this.aula && this.aula.id === lessonId) {
                        this.aula.current_time = parseFloat(data.current_time) || 0;
                        this.aula.progress_percentage = parseFloat(data.progress_percentage) || 0;
                        this.aula.last_accessed_at = data.last_accessed_at;

                        // Se progresso >= 95%, marcar como concluída automaticamente
                        if (data.progress_percentage >= 95 && !this.aula.completa) {
                            await this.marcarAulaComoConcluida(courseId, lessonId, true);
                        }
                    }

                    // Atualizar aula na lista de módulos e recalcular progresso do módulo
                    if (this.curso && this.curso.modulos) {
                        this.curso.modulos.forEach(modulo => {
                            const aula = modulo.aulas.find(a => a.id === lessonId);
                            if (aula) {
                                aula.current_time = parseFloat(data.current_time) || 0;
                                aula.progress_percentage = parseFloat(data.progress_percentage) || 0;
                                
                                // Recalcular progresso do módulo
                                this.recalcularProgressoModulo(modulo);
                            }
                        });
                    }

                    return data;
                }

                throw new Error('Resposta inválida');
            } catch (error) {
                const status = error?.response?.status;
                const message = error?.response?.data?.message;
                const isVideoOnlyMismatch = status === 400
                    && typeof message === 'string'
                    && message.includes('apenas para aulas do tipo vídeo');

                if (!isVideoOnlyMismatch) {
                    console.error('Erro ao salvar progresso:', error);
                }
            } finally {
                // Aguardar um pouco antes de permitir próximo save
                setTimeout(() => {
                    this.aulaProgressSaving = false;
                }, 1000);
            }
        },

        async salvarAnotacao(courseId, lessonId, content) {
            this.aulaNoteSaving = true;

            try {
                const aula = this.aula;
                const noteId = aula?.note?.id;

                let response;
                if (noteId) {
                    // Atualizar anotação existente
                    response = await axios.put(
                        `/api/v1/courses/${courseId}/lessons/${lessonId}/notes/${noteId}`,
                        { content }
                    );
                } else {
                    // Criar nova anotação
                    response = await axios.post(
                        `/api/v1/courses/${courseId}/lessons/${lessonId}/notes`,
                        { content }
                    );
                }

                // Verificar se a resposta indica erro mesmo com status 200
                if (response.data && response.data.success === false) {
                    // Extrair mensagem de erro da resposta
                    let errorMessage = response.data.message || 'Erro ao salvar anotação';
                    
                    // Se não tiver message direto, tentar pegar de errors
                    if (!errorMessage || errorMessage === 'Erro ao salvar anotação') {
                        if (response.data.errors) {
                            // Tentar pegar de errors.error
                            if (response.data.errors.error) {
                                errorMessage = response.data.errors.error;
                            }
                            // Tentar pegar de errors (objeto com múltiplos campos)
                            else if (typeof response.data.errors === 'object') {
                                const errorValues = Object.values(response.data.errors);
                                // Procurar primeiro valor que seja string
                                for (const errorValue of errorValues) {
                                    if (typeof errorValue === 'string' && errorValue) {
                                        errorMessage = errorValue;
                                        break;
                                    } else if (Array.isArray(errorValue) && errorValue.length > 0) {
                                        errorMessage = errorValue[0];
                                        break;
                                    }
                                }
                            }
                        }
                    }
                    
                    const error = new Error(errorMessage);
                    error.response = {
                        data: response.data,
                        status: response.status || 400
                    };
                    throw error;
                }

                if (response.data.success && response.data.data) {
                    // Atualizar anotação na aula local
                    if (this.aula && this.aula.id === lessonId) {
                        this.aula.note = response.data.data;
                    }

                    return response.data.data;
                }

                throw new Error('Resposta inválida');
            } catch (error) {
                console.error('Erro ao salvar anotação:', error);
                
                // Se for erro HTTP, garantir que a mensagem seja extraída corretamente
                if (error.response && error.response.data) {
                    const responseData = error.response.data;
                    
                    // Se não tiver mensagem no error.message, extrair da resposta
                    if (!error.message || error.message === 'Resposta inválida') {
                        let errorMessage = responseData.message || 'Erro ao salvar anotação';
                        
                        if (!errorMessage || errorMessage === 'Erro ao salvar anotação') {
                            if (responseData.errors && responseData.errors.error) {
                                errorMessage = responseData.errors.error;
                            } else if (responseData.errors && typeof responseData.errors === 'object') {
                                const errorValues = Object.values(responseData.errors);
                                for (const errorValue of errorValues) {
                                    if (typeof errorValue === 'string' && errorValue) {
                                        errorMessage = errorValue;
                                        break;
                                    } else if (Array.isArray(errorValue) && errorValue.length > 0) {
                                        errorMessage = errorValue[0];
                                        break;
                                    }
                                }
                            }
                        }
                        
                        error.message = errorMessage;
                    }
                }
                
                throw error;
            } finally {
                this.aulaNoteSaving = false;
            }
        },

        async obterQuiz(courseId, lessonId) {
            try {
                const response = await axios.get(`/api/v1/courses/${courseId}/lessons/${lessonId}/quiz`);

                if (response.data.success && response.data.data) {
                    return response.data.data;
                }

                throw new Error('Resposta inválida');
            } catch (error) {
                console.error('Erro ao obter quiz:', error);
                throw error;
            }
        },

        async submeterQuiz(courseId, lessonId, answers) {
            this.quizSubmitting = true;

            try {
                const response = await axios.post(
                    `/api/v1/courses/${courseId}/lessons/${lessonId}/quiz/submit`,
                    { answers }
                );

                // Verificar se a resposta indica erro mesmo com status 200
                if (response.data.success === false) {
                    const responseData = response.data;
                    let errorMessage = 'Erro ao submeter quiz';
                    
                    // Priorizar mensagem específica do erro
                    if (responseData.errors?.error) {
                        errorMessage = responseData.errors.error;
                    } else if (responseData.errors && typeof responseData.errors === 'object') {
                        // Pegar primeira mensagem de erro disponível
                        const firstError = Object.values(responseData.errors)[0];
                        if (Array.isArray(firstError) && firstError.length > 0) {
                            errorMessage = firstError[0];
                        } else if (typeof firstError === 'string') {
                            errorMessage = firstError;
                        }
                    } else if (responseData.message) {
                        errorMessage = responseData.message;
                    }
                    
                    const error = new Error(errorMessage);
                    error.response = { data: responseData };
                    throw error;
                }

                if (response.data.success && response.data.data) {
                    const data = response.data.data;

                    // Se score = 100%, marcar aula como concluída
                    if (data.score === 100 && data.completed) {
                        await this.marcarAulaComoConcluida(courseId, lessonId, true);
                    }

                    return data;
                }

                throw new Error('Resposta inválida');
            } catch (error) {
                console.error('Erro ao submeter quiz:', error);
                
                // Se o erro já tem mensagem específica, manter
                if (error.message && error.message !== 'Request failed with status code 500') {
                    throw error;
                }
                
                // Caso contrário, tentar extrair mensagem do response
                if (error.response && error.response.data) {
                    const responseData = error.response.data;
                    let errorMessage = 'Erro ao submeter quiz';
                    
                    // Priorizar mensagem específica do erro
                    if (responseData.errors?.error) {
                        errorMessage = responseData.errors.error;
                    } else if (responseData.errors && typeof responseData.errors === 'object') {
                        // Pegar primeira mensagem de erro disponível
                        const firstError = Object.values(responseData.errors)[0];
                        if (Array.isArray(firstError) && firstError.length > 0) {
                            errorMessage = firstError[0];
                        } else if (typeof firstError === 'string') {
                            errorMessage = firstError;
                        }
                    } else if (responseData.message) {
                        errorMessage = responseData.message;
                    }
                    
                    const customError = new Error(errorMessage);
                    customError.response = error.response;
                    throw customError;
                }
                
                throw error;
            } finally {
                this.quizSubmitting = false;
            }
        },

        async obterSimuladoAtual(courseId, lessonId) {
            this.simuladoLoading = true;

            try {
                const response = await axios.get(`/api/v1/courses/${courseId}/lessons/${lessonId}/simulado/current`);

                if (response.data?.success) {
                    return response.data.data || null;
                }

                if (response.data?.data !== undefined) {
                    return response.data.data;
                }

                throw new Error('Resposta inválida');
            } catch (error) {
                // 404/204 podem representar ausência de tentativa ativa dependendo da implementação
                if (error.response?.status === 404 || error.response?.status === 204) {
                    return null;
                }
                console.error('Erro ao consultar simulado atual:', error);
                throw error;
            } finally {
                this.simuladoLoading = false;
            }
        },

        async iniciarSimulado(courseId, lessonId) {
            this.simuladoLoading = true;

            try {
                const response = await axios.post(`/api/v1/courses/${courseId}/lessons/${lessonId}/simulado/start`);

                if (response.data?.success && response.data.data) {
                    return response.data.data;
                }

                if (response.data?.success === false) {
                    const responseData = response.data;
                    const errorMessage = responseData.message ||
                        responseData.errors?.error ||
                        'Não foi possível iniciar o simulado';
                    const customError = new Error(errorMessage);
                    customError.response = { data: responseData };
                    throw customError;
                }

                throw new Error('Resposta inválida');
            } catch (error) {
                console.error('Erro ao iniciar simulado:', error);
                throw error;
            } finally {
                this.simuladoLoading = false;
            }
        },

        async enviarRedacaoSimulado(courseId, lessonId, payload = {}) {
            this.simuladoSubmitting = true;

            try {
                const hasImage = !!payload.imagem;
                let requestBody = null;
                let config = {};

                if (hasImage) {
                    const formData = new FormData();
                    formData.append('attempt_id', payload.attempt_id);

                    if (payload.texto) {
                        formData.append('texto', payload.texto);
                    }
                    if (hasImage) {
                        formData.append('imagem', payload.imagem);
                    }

                    requestBody = formData;
                    config.headers = { 'Content-Type': 'multipart/form-data' };
                } else {
                    requestBody = {
                        attempt_id: payload.attempt_id,
                        texto: payload.texto || ''
                    };
                }

                const response = await axios.post(
                    `/api/v1/courses/${courseId}/lessons/${lessonId}/simulado/submit-essay`,
                    requestBody,
                    config
                );

                if (response.data?.success && response.data.data) {
                    return response.data.data;
                }

                if (response.data?.success === false) {
                    const responseData = response.data;
                    const errorMessage = responseData.message ||
                        responseData.errors?.error ||
                        'Não foi possível enviar a redação do simulado';
                    const customError = new Error(errorMessage);
                    customError.response = { data: responseData };
                    throw customError;
                }

                throw new Error('Resposta inválida');
            } catch (error) {
                console.error('Erro ao enviar redação do simulado:', error);
                throw error;
            } finally {
                this.simuladoSubmitting = false;
            }
        },

        async obterHistoricoSimulado(courseId, lessonId, limit = 20) {
            try {
                const response = await axios.get(
                    `/api/v1/courses/${courseId}/lessons/${lessonId}/simulado/history`,
                    { params: { limit } }
                );

                if (response.data?.success && response.data.data !== undefined) {
                    return response.data.data;
                }

                if (response.data?.data !== undefined) {
                    return response.data.data;
                }

                throw new Error('Resposta inválida');
            } catch (error) {
                console.error('Erro ao obter histórico do simulado:', error);
                throw error;
            }
        },

        limparAula() {
            this.aula = null;
            this.aulaError = null;
        },

        async baixarCertificado(courseId) {
            try {
                const response = await axios.get(
                    `/api/v1/courses/${courseId}/certificate/download`,
                    {
                        responseType: 'blob' // Importante para downloads de arquivo
                    }
                );

                // Criar URL do blob
                const blob = new Blob([response.data]);
                const url = window.URL.createObjectURL(blob);
                
                // Criar link temporário para download
                const link = document.createElement('a');
                link.href = url;
                
                // Tentar obter o nome do arquivo do header Content-Disposition
                const contentDisposition = response.headers['content-disposition'];
                let filename = 'certificado.pdf';
                
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1].replace(/['"]/g, '');
                        // Decodificar se estiver em formato URL
                        try {
                            filename = decodeURIComponent(filename);
                        } catch (e) {
                            // Se falhar, usar o nome original
                        }
                    }
                }
                
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                
                // Limpar
                link.remove();
                window.URL.revokeObjectURL(url);
                
                return true;
            } catch (error) {
                console.error('Erro ao baixar certificado:', error);
                
                // Se o erro for 404, o curso pode não estar concluído
                if (error.response?.status === 404) {
                    throw new Error('Certificado não disponível. Verifique se o curso está 100% concluído.');
                }
                
                // Se o erro for 403, o usuário não tem permissão
                if (error.response?.status === 403) {
                    throw new Error('Você não tem permissão para baixar este certificado.');
                }
                
                throw error;
            }
        },

        // ========== HELPER: Obter aula para iniciar/continuar curso ==========
        
        /**
         * Processa a distribuição de avaliações, convertendo objeto em array e calculando porcentagens
         * @param {Object|Array} distribuicao - Distribuição de avaliações (objeto {1: x, 2: y, ...} ou array)
         * @param {Number} totalAvaliacoes - Total de avaliações para calcular porcentagens
         * @returns {Array} Array de porcentagens na ordem [5, 4, 3, 2, 1] estrelas
         */
        processarDistribuicaoAvaliacoes(distribuicao, totalAvaliacoes) {
            // Se já for array, retornar como está (assumindo ordem correta)
            if (Array.isArray(distribuicao)) {
                return distribuicao;
            }
            
            // Se for objeto, converter para array
            if (distribuicao && typeof distribuicao === 'object') {
                // Criar array na ordem [5, 4, 3, 2, 1] estrelas
                const array = [];
                for (let i = 5; i >= 1; i--) {
                    const count = distribuicao[i] || distribuicao[String(i)] || 0;
                    // Calcular porcentagem se houver total de avaliações
                    const porcentagem = totalAvaliacoes > 0 
                        ? Math.round((count / totalAvaliacoes) * 100) 
                        : 0;
                    array.push(porcentagem);
                }
                return array;
            }
            
            // Fallback: array vazio
            return [0, 0, 0, 0, 0];
        },

        /**
         * Recalcula o progresso de um módulo baseado no progresso das aulas
         * @param {Object} modulo - Objeto do módulo
         */
        recalcularProgressoModulo(modulo) {
            if (!modulo || !modulo.aulas || modulo.aulas.length === 0) {
                modulo.progresso = 0;
                return;
            }

            // Calcular progresso médio das aulas
            const totalAulas = modulo.aulas.length;
            let somaProgresso = 0;

            modulo.aulas.forEach(aula => {
                // Se a aula está completa, considerar 100%
                if (aula.completa) {
                    somaProgresso += 100;
                } else {
                    // Caso contrário, usar o progress_percentage
                    somaProgresso += parseFloat(aula.progress_percentage) || 0;
                }
            });

            // Calcular média
            modulo.progresso = Math.round((somaProgresso / totalAulas) * 100) / 100;
        },

        /**
         * Retorna a aula que o usuário deve acessar ao iniciar/continuar o curso
         * Prioridade: 1) última aula acessada (last_lesson), 2) primeira aula do curso
         * @returns {Object|null} Objeto com { id, slug } da aula ou null se não houver aulas
         */
        obterAulaParaIniciar() {
            // Se houver última aula acessada, usar ela
            if (this.lastLesson && this.lastLesson.id) {
                return {
                    id: this.lastLesson.id,
                    slug: this.lastLesson.slug
                };
            }

            // Caso contrário, buscar primeira aula do primeiro módulo
            if (this.curso && this.curso.modulos && this.curso.modulos.length > 0) {
                const primeiroModulo = this.curso.modulos[0];
                if (primeiroModulo.aulas && primeiroModulo.aulas.length > 0) {
                    const primeiraAula = primeiroModulo.aulas[0];
                    return {
                        id: primeiraAula.id,
                        slug: primeiraAula.slug || primeiraAula.id
                    };
                }
            }

            return null;
        },

        // ========== ACTIONS PARA AVALIAÇÕES ==========

        async buscarMinhaAvaliacao(courseId) {
            try {
                const response = await axios.get(`/api/v1/courses/${courseId}/reviews/my`);

                if (response.data.success) {
                    const avaliacao = response.data.data;
                    
                    if (avaliacao) {
                        // Atualizar minha avaliação local
                        this.minhaAvaliacao = {
                            id: avaliacao.id,
                            rating: avaliacao.rating || 0,
                            comment: avaliacao.comment || '',
                            is_approved: avaliacao.is_approved !== undefined ? avaliacao.is_approved : null,
                            created_at: avaliacao.created_at || null,
                            updated_at: avaliacao.updated_at || null,
                            user: avaliacao.user || null
                        };
                    } else {
                        // Usuário ainda não avaliou
                        this.minhaAvaliacao = null;
                    }
                    
                    return this.minhaAvaliacao;
                }

                throw new Error('Resposta inválida');
            } catch (error) {
                console.error('Erro ao buscar minha avaliação:', error);
                
                // Se for erro 401 (não autenticado), apenas limpar
                if (error.response?.status === 401) {
                    this.minhaAvaliacao = null;
                    return null;
                }
                
                throw error;
            }
        },

        async buscarAvaliacoes(courseId) {
            try {
                const response = await axios.get(`/api/v1/courses/${courseId}/reviews`);

                if (response.data.success && response.data.data) {
                    const avaliacoes = Array.isArray(response.data.data) 
                        ? response.data.data 
                        : (response.data.data.data || []);
                    
                    // Atualizar lista de avaliações do curso
                    if (this.curso && this.curso.id === courseId) {
                        this.curso.avaliacoes = avaliacoes;
                    }
                    
                    return avaliacoes;
                }

                throw new Error('Resposta inválida');
            } catch (error) {
                console.error('Erro ao buscar avaliações:', error);
                throw error;
            }
        },

        async criarAvaliacao(courseId, rating, comment = '') {
            this.avaliacaoSubmitting = true;
            try {
                const response = await axios.post('/api/v1/courses/reviews', {
                    course_id: courseId,
                    rating: rating,
                    comment: comment
                });

                if (response.data.success && response.data.data) {
                    const avaliacao = response.data.data;
                    
                    // Atualizar minha avaliação local
                    this.minhaAvaliacao = {
                        id: avaliacao.id,
                        rating: avaliacao.rating || 0,
                        comment: avaliacao.comment || '',
                        is_approved: avaliacao.is_approved !== undefined ? avaliacao.is_approved : null,
                        created_at: avaliacao.created_at || null,
                        updated_at: avaliacao.updated_at || null,
                        user: avaliacao.user || null
                    };
                    
                    // Adicionar à lista de avaliações do curso se não estiver lá
                    if (this.curso && this.curso.avaliacoes) {
                        // Verificar se já existe
                        const index = this.curso.avaliacoes.findIndex(a => a.id === avaliacao.id);
                        if (index === -1) {
                            this.curso.avaliacoes.unshift(avaliacao);
                        }
                    }
                    
                    // Recarregar avaliação do usuário usando endpoint dedicado para garantir dados atualizados
                    await this.buscarMinhaAvaliacao(courseId).catch(err => {
                        console.warn('Não foi possível recarregar avaliação:', err);
                    });
                    
                    // Recarregar curso completo para atualizar média e distribuição de avaliações
                    await this.obterCurso(courseId).catch(err => {
                        console.warn('Não foi possível recarregar curso:', err);
                    });
                    
                    return avaliacao;
                }

                throw new Error('Resposta inválida');
            } catch (error) {
                console.error('Erro ao criar avaliação:', error);
                throw error;
            } finally {
                this.avaliacaoSubmitting = false;
            }
        },

        async atualizarAvaliacao(reviewId, rating, comment = '') {
            this.avaliacaoSubmitting = true;
            try {
                const response = await axios.put(`/api/v1/courses/reviews/${reviewId}`, {
                    rating: rating,
                    comment: comment
                });

                if (response.data.success && response.data.data) {
                    const avaliacao = response.data.data;
                    
                    // Atualizar minha avaliação local
                    this.minhaAvaliacao = {
                        id: avaliacao.id,
                        rating: avaliacao.rating || 0,
                        comment: avaliacao.comment || '',
                        is_approved: avaliacao.is_approved !== undefined ? avaliacao.is_approved : null,
                        created_at: avaliacao.created_at || null,
                        updated_at: avaliacao.updated_at || null,
                        user: avaliacao.user || null
                    };
                    
                    // Atualizar na lista de avaliações do curso
                    if (this.curso && this.curso.avaliacoes) {
                        const index = this.curso.avaliacoes.findIndex(a => a.id === reviewId);
                        if (index !== -1) {
                            this.curso.avaliacoes[index] = avaliacao;
                        }
                    }
                    
                    // Recarregar avaliação do usuário usando endpoint dedicado para garantir dados atualizados
                    // Precisamos do courseId, então vamos buscar do curso atual
                    if (this.curso && this.curso.id) {
                        await this.buscarMinhaAvaliacao(this.curso.id).catch(err => {
                            console.warn('Não foi possível recarregar avaliação:', err);
                        });
                        
                        // Recarregar curso completo para atualizar média e distribuição de avaliações
                        await this.obterCurso(this.curso.id).catch(err => {
                            console.warn('Não foi possível recarregar curso:', err);
                        });
                    }
                    
                    return avaliacao;
                }

                throw new Error('Resposta inválida');
            } catch (error) {
                console.error('Erro ao atualizar avaliação:', error);
                throw error;
            } finally {
                this.avaliacaoSubmitting = false;
            }
        },

        async deletarAvaliacao(reviewId) {
            try {
                const response = await axios.delete(`/api/v1/courses/reviews/${reviewId}`);

                // Status 204 (No Content) ou resposta com success: true são considerados sucesso
                if (response.status === 204 || (response.data && response.data.success)) {
                    // Remover minha avaliação local
                    this.minhaAvaliacao = null;
                    
                    // Remover da lista de avaliações do curso
                    if (this.curso && this.curso.avaliacoes) {
                        const index = this.curso.avaliacoes.findIndex(a => a.id === reviewId);
                        if (index !== -1) {
                            this.curso.avaliacoes.splice(index, 1);
                        }
                    }
                    
                    // Recarregar avaliação do usuário usando endpoint dedicado para garantir dados atualizados
                    // Precisamos do courseId, então vamos buscar do curso atual
                    if (this.curso && this.curso.id) {
                        await this.buscarMinhaAvaliacao(this.curso.id).catch(err => {
                            console.warn('Não foi possível recarregar avaliação:', err);
                        });
                        
                        // Recarregar curso completo para atualizar média e distribuição de avaliações
                        await this.obterCurso(this.curso.id).catch(err => {
                            console.warn('Não foi possível recarregar curso:', err);
                        });
                    }
                    
                    return true;
                }

                throw new Error('Resposta inválida');
            } catch (error) {
                console.error('Erro ao deletar avaliação:', error);
                throw error;
            }
        },
    },
});
